import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ActionTemplateParameterResource, PropertyValueResource, VariableType, WorkerPoolResource } from "@octopusdeploy/octopus-server-client";
import ChangedValueIndicator from "~/areas/projects/components/Variables/TenantVariables/ChangedValueIndicator";
import CommonTemplateValueEdit from "~/areas/projects/components/Variables/TenantVariables/CommonTemplateValueEdit";
import DefaultValueIndicator from "~/areas/projects/components/Variables/TenantVariables/DefaultValueIndicator";
import ReadonlyInlineVariableValue from "~/areas/projects/components/Variables/TenantVariables/ReadonlyInlineVariableValue";
import type { CommonTemplatesChangeTracker, CommonTemplatesKey } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesChangeTracker";
import type { LoadedSourceItems } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableResourceOptions";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import type { PopoverPosition } from "~/components/PopoverWhenFocused/PopoverWhenFocused";
import PopoverWhenFocused from "~/components/PopoverWhenFocused/index";
interface CommonTemplateValueCellProps {
    template: ActionTemplateParameterResource;
    accounts: {
        [accountId: string]: string;
    };
    certificates: {
        [certificateId: string]: string;
    };
    workerPools: {
        [workerPoolId: string]: WorkerPoolResource;
    };
    value: PropertyValueResource | undefined;
    valueType: VariableType;
    sourceItems: LoadedSourceItems;
    doBusyTask: DoBusyTask;
    changeTracker: CommonTemplatesChangeTracker;
    valueKey: CommonTemplatesKey;
    focused: boolean;
    onFocus: () => void;
    onBlur: () => void;
    bindableTemplateNames: string[];
    hasEditPermissions: boolean;
    hasValueChanged: boolean;
    initialValue: PropertyValueResource | undefined;
    initialEnvironmentIds: string[];
}
export default function CommonTemplateValueInlineCell({ template, value, valueType, accounts, certificates, workerPools, sourceItems, doBusyTask, changeTracker, valueKey, focused, onFocus, onBlur, bindableTemplateNames, hasEditPermissions, hasValueChanged, initialValue, initialEnvironmentIds, }: CommonTemplateValueCellProps) {
    return hasEditPermissions ? (<CommonTemplateValueEditableCell template={template} accounts={accounts} certificates={certificates} workerPools={workerPools} value={value} valueType={valueType} sourceItems={sourceItems} doBusyTask={doBusyTask} changeTracker={changeTracker} valueKey={valueKey} focused={focused} onFocus={onFocus} onBlur={onBlur} hasEditPermissions={hasEditPermissions} bindableTemplateNames={bindableTemplateNames} hasValueChanged={hasValueChanged} initialValue={initialValue} initialEnvironmentIds={initialEnvironmentIds}/>) : (<CommonTemplateValueReadonlyCell template={template} value={value} valueType={valueType} accounts={accounts} certificates={certificates} workerPools={workerPools} hasEditPermissions={hasEditPermissions} sourceItems={sourceItems} hasValueChanged={hasValueChanged}/>);
}
interface CommonTemplateValueEditableCellProps extends CommonTemplateValueCellProps {
    hasEditPermissions: boolean;
}
function CommonTemplateValueEditableCell({ template, value, valueType, accounts, certificates, workerPools, sourceItems, doBusyTask, changeTracker, valueKey, focused, onFocus, onBlur, hasEditPermissions, bindableTemplateNames, hasValueChanged, initialValue, initialEnvironmentIds, }: CommonTemplateValueEditableCellProps) {
    const calculateFocusedPopoverPosition = (): PopoverPosition => {
        // Assumes parent's padding, How to retrieve padding values from the parent
        return {
            top: -12,
            left: 0,
            right: 0,
        };
    };
    return (<div onFocus={onFocus} onClick={onFocus} className={cellStyles.container}>
            <PopoverWhenFocused isFocused={focused} onClickOutside={onBlur} dynamicPosition={calculateFocusedPopoverPosition}>
                {focused ? (<CommonTemplateValueEdit template={template} value={value} valueKey={valueKey} changeTracker={changeTracker} sourceItems={sourceItems} doBusyTask={doBusyTask} bindableTemplateNames={bindableTemplateNames} initialValue={initialValue} initialEnvironmentIds={initialEnvironmentIds}/>) : (<CommonTemplateValueReadonlyCell template={template} value={value} valueType={valueType} accounts={accounts} certificates={certificates} workerPools={workerPools} hasEditPermissions={hasEditPermissions} sourceItems={sourceItems} hasValueChanged={hasValueChanged}/>)}
            </PopoverWhenFocused>
        </div>);
}
interface CommonTemplateValueReadonlyCellProps {
    template: ActionTemplateParameterResource;
    value: PropertyValueResource | undefined;
    valueType: VariableType;
    accounts: {
        [accountId: string]: string;
    };
    certificates: {
        [certificateId: string]: string;
    };
    workerPools: {
        [workerPoolId: string]: WorkerPoolResource;
    };
    hasEditPermissions: boolean;
    sourceItems: LoadedSourceItems;
    hasValueChanged: boolean;
}
function CommonTemplateValueReadonlyCell({ template, value, valueType, accounts, certificates, workerPools, hasEditPermissions, sourceItems, hasValueChanged }: CommonTemplateValueReadonlyCellProps) {
    const hasDefaultValue = !!(!value && template.DefaultValue);
    return (<div className={cellStyles.readonlyContainer}>
            <ReadonlyInlineVariableValue value={value} variableType={valueType} defaultValue={template.DefaultValue} accounts={accounts} certificates={certificates} workerPools={workerPools} hasEditPermissions={hasEditPermissions} sourceItems={sourceItems}/>
            <DefaultValueIndicator show={hasDefaultValue}/>
            <ChangedValueIndicator show={hasValueChanged}/>
        </div>);
}
const cellStyles = {
    container: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "4rem",
    }),
    readonlyContainer: css({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: space[8],
        width: "100%",
        height: "100%",
        padding: `${space[8]} ${space[12]}`,
    }),
};
